import { useEffect, useRef, useState, useCallback, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    setSelectedIds,
    toggleSelectedId,
    updateShape,
    removeShapes,
} from '../../../../../store/slices/drawingSlice';

const HANDLES = [
    { top: -4,                left:  -4               },
    { top: -4,                left:  'calc(50% - 4px)'},
    { top: -4,                right: -4               },
    { top: 'calc(50% - 4px)', left:  -4               },
    { top: 'calc(50% - 4px)', right: -4               },
    { bottom: -4,             left:  -4               },
    { bottom: -4,             left:  'calc(50% - 4px)'},
    { bottom: -4,             right: -4               },
];

const HANDLE_CURSORS = [
    'nwse-resize',  
    'ns-resize',    
    'nesw-resize',  
    'ew-resize',    
    'ew-resize',    
    'nesw-resize',  
    'ns-resize',    
    'nwse-resize',  
];

const MIN_SIZE = 40;

const SELECTION_OUTSET = 10;
 

const EditTextarea = forwardRef(function EditTextarea(
    { value, onChange, onCommit, onCancel, textStyle, fixedWidth, fixedHeight, disableDelete },
    ref
) {

    const hasFixed = fixedWidth > 0 || fixedHeight > 0;

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
        if (!hasFixed) autoHeight(el);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const autoHeight = (el) => {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
    };

    return (
        <textarea
            ref={ref}
            value={value}
            onChange={e => {
                const next = e.target.value;
                // When disableDelete is on, reject any change that makes the text shorter.
                if (disableDelete && next.length < value.length) return;
                onChange(next);
                if (!hasFixed) autoHeight(e.target);
            }}
            onBlur={onCommit}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            onDoubleClick={e => e.stopPropagation()}
            onKeyDown={e => {
                e.stopPropagation();
                if (e.key === 'Escape') { e.preventDefault(); onCancel(); return; }
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onCommit(); return; }
            }}
            style={{
                ...textStyle,
                width:      fixedWidth  > 0 ? `${fixedWidth}px`  : '100%',
                height:     fixedHeight > 0 ? `${fixedHeight}px` : undefined,
                minWidth:   fixedWidth  > 0 ? undefined : '80px',
                resize:     'none',
                overflow:   fixedHeight > 0 ? 'auto' : 'hidden',
                outline:    'none',
                border:     '1px dashed #4A90D9',
                background: 'rgba(255,255,255,0.95)',
                padding:    '2px 6px',
                boxSizing:  'border-box',
                display:    'block',
                cursor:     'text',
            }}
        />
    );
});

export default function TextOverlay() {
    const dispatch     = useDispatch();
    const map          = useSelector(s => s.map.mapContainer);
    const shapes       = useSelector(s => s.drawing.shapes);
    const selectedIds  = useSelector(s => s.drawing.selectedIds);
    const activeTool   = useSelector(s => s.drawingToolbar.activeTool);
    const currentFloor = useSelector(s => s.api.currentFloor);

    const [, setTick]       = useState(0);
    const [, setResizeTick] = useState(0);
    const refresh = useCallback(() => setTick(n => n + 1), []);

    useEffect(() => {
        if (!map) return;
        map.on('move',   refresh);
        map.on('zoom',   refresh);
        map.on('rotate', refresh);
        return () => {
            map.off('move',   refresh);
            map.off('zoom',   refresh);
            map.off('rotate', refresh);
        };
    }, [map, refresh]);
 
    const textRefs  = useRef(new Map()); 
    const shapesRef = useRef(shapes);
    useEffect(() => { shapesRef.current = shapes; }, [shapes]);

    const dragRef       = useRef({ active: false });
    const resizeDragRef = useRef({ active: false });
    const [, setDragTick] = useState(0);

    const [editingId, setEditingId]   = useState(null);
    const [editValue, setEditValue]   = useState('');
    // Ref mirrors — updated synchronously so event handlers always see the
    // latest values even before React flushes batched state updates.
    const editingIdRef  = useRef(null);
    const editValueRef  = useRef('');
    // Direct ref to the textarea DOM element so we can re-focus after sub-tool clicks.
    const textareaRef = useRef(null);

    // Clicking on empty map area deselects all text shapes.
    useEffect(() => {
        if (!map) return;
        const handleMapClick = () => {
            if (!editingId) {
                dispatch(setSelectedIds([]));
            }
        };
        map.on('click', handleMapClick);
        return () => map.off('click', handleMapClick);
    }, [map, dispatch, editingId]);
    // Keep editValueRef in sync with editValue state.
    useEffect(() => { editValueRef.current = editValue; }, [editValue]);

    const editCommittedRef     = useRef(false);
    const lastCommittedTextRef = useRef({});
    // Flag set when a mousedown fires outside the map container (e.g. toolbar sub-tool click)
    // so we can prevent the textarea blur from committing and closing editing.
    const preventBlurRef = useRef(false);

    // While editing, watch for mousedown on toolbar sub-tool buttons (outside the map).
    // If detected, set preventBlurRef so the blur handler keeps editing open and re-focuses.
    // IMPORTANT: skip this logic if the click is on another text shape — in that case
    // we want blur to commit normally so the new shape's click can open editing.
    useEffect(() => {
        if (!editingId) return;
        const onDocMouseDown = (e) => {
            // If the click lands on any text shape overlay, let blur fire normally.
            const isOnTextShape = [...textRefs.current.values()].some(
                el => el && el.contains(e.target)
            );
            if (isOnTextShape) return;

            // If the click lands on the active textarea itself, also skip.
            if (textareaRef.current && textareaRef.current.contains(e.target)) return;

            const mapRoot = document.querySelector('.app');
            if (mapRoot && !mapRoot.contains(e.target)) {
                preventBlurRef.current = true;
                // Re-focus the textarea THEN reset the flag, so the user
                // can continue typing immediately after the sub-tool click.
                requestAnimationFrame(() => {
                    textareaRef.current?.focus();
                    preventBlurRef.current = false;
                });
            }
        };
        document.addEventListener('mousedown', onDocMouseDown, true);
        return () => document.removeEventListener('mousedown', onDocMouseDown, true);
    }, [editingId]);

    // When the user switches to a different tool while editing, commit and close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!editingId) return;
        editCommittedRef.current = false; // ensure commitEdit can run
        commitEdit();
    }, [activeTool]); // intentionally only activeTool — fires only on tool change

    const commitEdit = useCallback(() => {
        // If blur was triggered by a toolbar sub-tool click, keep editing open.
        if (preventBlurRef.current) return;
        if (editCommittedRef.current) return;
        editCommittedRef.current = true;

        // Read current values from refs — always up-to-date, no stale closures.
        const currentId   = editingIdRef.current;
        const currentText = editValueRef.current;

        // Update refs synchronously before the async state updates flush.
        editingIdRef.current = null;
        editValueRef.current = '';

        // Simple direct setters — no nested setState anti-pattern.
        setEditingId(null);
        setEditValue('');

        if (currentText.trim() && currentId) {
            lastCommittedTextRef.current[currentId] = currentText;
            dispatch(updateShape({ id: currentId, properties: { text: currentText } }));
        }
    }, [dispatch]);

    const cancelEdit = useCallback(() => {
        editCommittedRef.current = true;
        editingIdRef.current = null;
        editValueRef.current = '';
        setEditingId(null);
        setEditValue('');
    }, []);

    // Opens editing for the given shape id (shared by double-click and text-tool single click).
    const openEdit = useCallback((e, shapeId) => {
        e.stopPropagation();
        e.preventDefault();

        const id    = String(shapeId);
        const shape = shapesRef.current.find(s => String(s.id) === id);
        if (!shape) return;

        dispatch(setSelectedIds([id]));
        editCommittedRef.current = false;
        editingIdRef.current = id;
        const initialText = lastCommittedTextRef.current[id] !== undefined
            ? lastCommittedTextRef.current[id]
            : (shape.properties.text ?? '');
        editValueRef.current = initialText;
        setEditingId(id);
        setEditValue(initialText);
    }, [dispatch]);

    const handleDoubleClick = useCallback((e, shapeId) => {
        if (activeTool !== 'select') return;
        openEdit(e, shapeId);
    }, [activeTool, openEdit]);

    const handleClick = useCallback((e, shapeId) => {
        e.stopPropagation();

        const id = String(shapeId);

        if (activeTool === 'eraser') {
            if (!editingIdRef.current) dispatch(removeShapes([id]));
            return;
        }

        if (activeTool === 'select') {
            if (editingIdRef.current) return;
            if (e.shiftKey) { dispatch(toggleSelectedId(id)); }
            else            { dispatch(setSelectedIds([id])); }
            return;
        }

        if (activeTool === 'text') {
            // If a previous edit session is somehow still flagged (e.g. preventBlur
            // blocked the blur-commit), force-commit it now before opening the new one.
            if (editingIdRef.current && editingIdRef.current !== id) {
                editCommittedRef.current = false;
                preventBlurRef.current  = false;
                commitEdit();
            }
            if (!editingIdRef.current) {
                openEdit(e, shapeId);
            }
        }
    }, [activeTool, dispatch, commitEdit, openEdit]);
 
    const handleMouseDown = useCallback((e, shapeId) => {
        if (activeTool !== 'select') return;
        if (editingId) return;
        e.stopPropagation();
        e.preventDefault();

        const id    = String(shapeId);
        const shape = shapes.find(s => String(s.id) === id);
        if (!shape) return;

        dispatch(setSelectedIds([id]));

        dragRef.current = {
            active:     true,
            shapeId:    id,
            startX:     e.clientX,
            startY:     e.clientY,
            origCoords: shape.geometry.coordinates,
            liveLngLat: null,
        };

        document.body.style.cursor = 'move';

        const onMouseMove = (mv) => {
            const dr = dragRef.current;
            if (!dr.active) return;

            const dx     = mv.clientX - dr.startX;
            const dy     = mv.clientY - dr.startY;
            const origPx = map.project(dr.origCoords);
            const newPx  = { x: origPx.x + dx, y: origPx.y + dy };
            const ll     = map.unproject([newPx.x, newPx.y]);

            dragRef.current.liveLngLat = [ll.lng, ll.lat];
            setDragTick(n => n + 1);
        };

        const onMouseUp = () => {
            const dr = dragRef.current;
            if (dr.active && dr.liveLngLat) {
                dispatch(updateShape({
                    id:       dr.shapeId,
                    geometry: { type: 'Point', coordinates: dr.liveLngLat },
                }));
            }
            dragRef.current            = { active: false };
            document.body.style.cursor = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup',   onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup',   onMouseUp);
    }, [activeTool, dispatch, editingId, map, shapes]);
 
    const handleResizeMouseDown = useCallback((e, shapeId, handleIndex) => {
        e.stopPropagation();
        e.preventDefault();

        const id    = String(shapeId);
        const shape = shapes.find(s => String(s.id) === id);
        if (!shape) return;
 
        dispatch(setSelectedIds([id]));
 
        const el          = textRefs.current.get(id);
        const rect        = el?.getBoundingClientRect();
        const startWidth  = shape.properties.width  || (rect?.width  ?? 120);
        const startHeight = shape.properties.height || (rect?.height ?? 30);

        const startPixel = map.project(shape.geometry.coordinates);

        resizeDragRef.current = {
            active:      true,
            shapeId:     id,
            handleIndex,
            startX:      e.clientX,
            startY:      e.clientY,
            startPixelX: startPixel.x,
            startPixelY: startPixel.y,
            startWidth,
            startHeight,
            origCoords:  shape.geometry.coordinates,
            liveWidth:   startWidth,
            liveHeight:  startHeight,
            liveLngLat:  null,   
        };

        document.body.style.cursor = HANDLE_CURSORS[handleIndex];

        const onMouseMove = (mv) => {
            const dr = resizeDragRef.current;
            if (!dr.active) return;

            const dx = mv.clientX - dr.startX;
            const dy = mv.clientY - dr.startY;

            let newWidth   = dr.startWidth;
            let newHeight  = dr.startHeight;
            let newPixelX  = dr.startPixelX;
            let newPixelY  = dr.startPixelY;
            let posChanged = false;

            switch (dr.handleIndex) {
                case 0:  
                    newWidth   = Math.max(MIN_SIZE, dr.startWidth  - dx);
                    newHeight  = Math.max(MIN_SIZE, dr.startHeight - dy);
                    newPixelX  = dr.startPixelX + dr.startWidth  - newWidth;
                    newPixelY  = dr.startPixelY + dr.startHeight - newHeight;
                    posChanged = true;
                    break;
                case 1:  
                    newHeight  = Math.max(MIN_SIZE, dr.startHeight - dy);
                    newPixelY  = dr.startPixelY + dr.startHeight - newHeight;
                    posChanged = true;
                    break;
                case 2:  
                    newWidth   = Math.max(MIN_SIZE, dr.startWidth  + dx);
                    newHeight  = Math.max(MIN_SIZE, dr.startHeight - dy);
                    newPixelY  = dr.startPixelY + dr.startHeight - newHeight;
                    posChanged = true;
                    break;
                case 3:  
                    newWidth   = Math.max(MIN_SIZE, dr.startWidth  - dx);
                    newPixelX  = dr.startPixelX + dr.startWidth  - newWidth;
                    posChanged = true;
                    break;
                case 4: 
                    newWidth   = Math.max(MIN_SIZE, dr.startWidth  + dx);
                    break;
                case 5: 
                    newWidth   = Math.max(MIN_SIZE, dr.startWidth  - dx);
                    newHeight  = Math.max(MIN_SIZE, dr.startHeight + dy);
                    newPixelX  = dr.startPixelX + dr.startWidth  - newWidth;
                    posChanged = true;
                    break;
                case 6:  
                    newHeight  = Math.max(MIN_SIZE, dr.startHeight + dy);
                    break;
                case 7:  
                    newWidth   = Math.max(MIN_SIZE, dr.startWidth  + dx);
                    newHeight  = Math.max(MIN_SIZE, dr.startHeight + dy);
                    break;
                default: break;
            }

            resizeDragRef.current.liveWidth  = newWidth;
            resizeDragRef.current.liveHeight = newHeight;

            if (posChanged) {
                const ll = map.unproject([newPixelX, newPixelY]);
                resizeDragRef.current.liveLngLat = [ll.lng, ll.lat];
            }

            setResizeTick(n => n + 1);
        };

        const onMouseUp = () => {
            const dr = resizeDragRef.current;
            if (dr.active) { 
                const finalWidth   = Math.round(dr.liveWidth);
                const finalHeight  = Math.round(dr.liveHeight);
                const finalLngLat  = dr.liveLngLat;
                const finalShapeId = dr.shapeId;
 
                resizeDragRef.current      = { active: false };
                document.body.style.cursor = '';
 
                dispatch(updateShape({
                    id:         finalShapeId,
                    properties: { width: finalWidth, height: finalHeight },
                    ...(finalLngLat ? { geometry: { type: 'Point', coordinates: finalLngLat } } : {}),
                }));
            } else {
                resizeDragRef.current      = { active: false };
                document.body.style.cursor = '';
            }

            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup',   onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup',   onMouseUp);
    }, [dispatch, map, shapes]);

    if (!map) return null;

    const floorId    = currentFloor?.enc_id ?? null;
    const textShapes = shapes.filter(
        s => s.properties?.shapeType === 'text' && s.properties?.floorId === floorId,
    );
    const canInteract = activeTool === 'select' || activeTool === 'eraser' || activeTool === 'text';

    return (
        <div style={{
            position:      'absolute',
            inset:         0,
            pointerEvents: 'none',
            overflow:      'hidden',
            zIndex:        10,
        }}>
            {textShapes.map(shape => {
                const id         = String(shape.id);
                const [lng, lat] = shape.geometry.coordinates;
                const p          = shape.properties;
                const isSelected = selectedIds.includes(id);
                const isEditing  = editingId === id;
 
                const dr  = dragRef.current;
                const rdr = resizeDragRef.current;

                const liveCoords =
                    (dr.active  && dr.shapeId  === id && dr.liveLngLat)  ? dr.liveLngLat  :
                    (rdr.active && rdr.shapeId === id && rdr.liveLngLat) ? rdr.liveLngLat :
                    [lng, lat];

                const pixel = map.project(liveCoords);
 
                const liveWidth  = (rdr.active && rdr.shapeId === id) ? rdr.liveWidth  : p.width;
                const liveHeight = (rdr.active && rdr.shapeId === id) ? rdr.liveHeight : p.height;

                const hasWidth  = liveWidth  > 0;
                const hasHeight = liveHeight > 0;

                const textStyle = {
                    fontFamily: p.fontFamily  || 'Arial',
                    fontSize:   `${p.fontSize || 14}px`,
                    fontWeight: p.bold        ? 'bold' : 'normal',
                    color:      p.strokeColor || '#000000',
                    textAlign:  p.textAlign   || 'left',
                    lineHeight: 1.2,
                    '--map-font': p.fontFamily || 'Arial',
                };

                return ( 
                    <div
                        key={id}
                        style={{
                            position:      'absolute', 
                            left:          Math.round(pixel.x) - SELECTION_OUTSET,
                            top:           Math.round(pixel.y) - SELECTION_OUTSET,
                            padding:       SELECTION_OUTSET, 
                            width:         hasWidth  ? `${Math.round(liveWidth)}px`  : undefined,
                            height:        hasHeight ? `${Math.round(liveHeight)}px` : undefined,
                            boxSizing:     'content-box', 
                            overflow:      'visible',
                            pointerEvents: (canInteract || isEditing) ? 'auto' : 'none',
                            cursor: isEditing
                                ? 'text'
                                : activeTool === 'text'
                                    ? 'text'
                                    : activeTool === 'select'
                                        ? 'move'
                                        : activeTool === 'eraser'
                                            ? 'cell'
                                            : 'default',
                            userSelect: 'none',
                        }}
                        ref={el => {
                            if (el) textRefs.current.set(id, el);
                            else    textRefs.current.delete(id);
                        }}
                        onClick={e => { 
                            if (isEditing) { e.stopPropagation(); return; }
                            handleClick(e, shape.id);
                        }}
                        onDoubleClick={e => { 
                            if (isEditing) { e.stopPropagation(); return; }
                            handleDoubleClick(e, shape.id);
                        }}
                        onMouseDown={e => { 
                            if (isEditing) { e.stopPropagation(); return; }
                            handleMouseDown(e, shape.id);
                        }}
                    > 
                        <div
                            style={{
                                position: 'relative',
                                width:    hasWidth  ? '100%' : undefined,
                                height:   hasHeight ? '100%' : undefined,
                                overflow: (hasWidth || hasHeight) ? 'hidden' : 'visible',
                            }}
                        >
                            {isEditing ? (
                                <EditTextarea
                                    ref={textareaRef}
                                    value={editValue}
                                    onChange={v => { editValueRef.current = v; setEditValue(v); }}
                                    onCommit={commitEdit}
                                    onCancel={cancelEdit}
                                    textStyle={textStyle}
                                    fixedWidth={hasWidth  ? Math.round(liveWidth)  : 0}
                                    fixedHeight={hasHeight ? Math.round(liveHeight) : 0}
                                    disableDelete={activeTool === 'text'}
                                />
                            ) : (
                                <span
                                    title={activeTool === 'select' ? 'Double-click to edit' : undefined}
                                    className="map-text"
                                    style={{
                                        ...textStyle,
                                        display:    'block',
                                        whiteSpace: hasWidth ? 'pre-wrap' : 'pre',
                                        wordBreak:  hasWidth ? 'break-word' : undefined,
                                        width:      hasWidth  ? '100%' : undefined,
                                        height:     hasHeight ? '100%' : undefined,
                                        WebkitFontSmoothing: 'antialiased',
                                    }}
                                >
                                    {p.text}
                                </span>
                            )}
                        </div>
 
                        {isSelected && (
                            <div
                                style={{
                                    position:      'absolute',
                                    inset:         0,
                                    border:        '1.5px solid #4A90D9',
                                    borderRadius:  2,
                                    pointerEvents: 'none',
                                    boxSizing:     'border-box',
                                    zIndex:        1,
                                }}
                                onMouseDown={e => { e.stopPropagation(); e.preventDefault(); }}
                                onClick={e => e.stopPropagation()}
                            > 
                                {!isEditing && HANDLES.map((pos, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            position:      'absolute',
                                            width:         8,
                                            height:        8,
                                            background:    '#ffffff',
                                            border:        '1.5px solid #4A90D9',
                                            borderRadius:  1,
                                            boxSizing:     'border-box',
                                            cursor:        HANDLE_CURSORS[i],
                                            pointerEvents: 'auto',
                                            zIndex:        2,
                                            ...pos,
                                        }}
                                        onMouseDown={e => handleResizeMouseDown(e, shape.id, i)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}